import { Component, EventEmitter, Input, OnInit, Output, inject, DestroyRef } from '@angular/core';
import { Router } from '@angular/router';
import { RouterSegments } from 'src/app/models/router-segments';
import { AuthService } from 'src/app/services/auth/auth.service';
import { ServiceCardInfo } from '../../../models/service/service-card-info';
import { ServiceStatus } from '../../../models/service/service-status';
import { ChangeTaskService } from '../../../services/api/change-task.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ChangeState } from '../../../models/service/change-state';
import { ChangeTask } from 'src/app/models/service/change-task';
import { ConfirmationDialogComponent } from 'src/app/components/confirmation-dialog/confirmation-dialog.component';
import { translate } from '@jsverse/transloco';
import { MatDialog } from '@angular/material/dialog';
import { BlueprintsService } from 'src/app/services/api/blueprints.service';
import { combineLatest, finalize, tap } from 'rxjs';
import { Permission } from 'src/app/models/principal/permission';
import { PermissionsService } from 'src/app/services/util/permissions.service';
import {TrialStatusService} from '../../../services/util/trial-status.service';
import {TenantDetails} from '../../../models/tenant-details';
import {ErrorDialogComponent} from 'src/app/components/error-dialog/error-dialog.component';
import { TenantsService } from 'src/app/services/api/tenants.service';
import {SolutionsService} from 'src/app/services/api/solutions.service';
import { SolutionVersionsService } from 'src/app/services/util/solution-versions.service';
import {DeploymentClass} from '../../../models/instance-class';
import { ToastrService } from 'src/app/services/util/toastr.service';


const REQUIRED_PERMISSIONS: Permission[] = [
  Permission.DELETE_SERVICE_DEFINITION,
];

@Component({
    selector: 'app-service-card',
    templateUrl: './service-card.component.html',
    styleUrls: ['./service-card.component.scss'],
    standalone: false
})
export class ServiceCardComponent implements OnInit {
  @Input() loader: boolean;
  @Input() serviceCardInfo: ServiceCardInfo;
  @Input() tenantDetails?: TenantDetails;
  @Input() isSandboxCard?: boolean;
  @Input() serviceVersions;

  @Output() blueprintDeleted = new EventEmitter<ServiceCardInfo>();
private destroyRef = inject(DestroyRef);
  readonly statusInfo: { [key: string]: any } = {
    [ServiceStatus.LIVE]: {icon: 'done', colorClass: 'green-1-bg'},
    [ServiceStatus.IN_PROGRESS]: {icon: 'change_circle', colorClass: 'accent-1-bg'},
    [ServiceStatus.DOWN]: {icon: 'info', colorClass: 'warn-solid-bg'},
  };

  deploymentClass = DeploymentClass;
  ServiceStatus = ServiceStatus;
  ChangeState = ChangeState;
  isUpdateAvailable: boolean;
  isComingSoon: boolean;
  changeTask: ChangeTask;
  isLoading = false;

  permissionsMap: {[key: string]: boolean};
  Permission = Permission;
  trialStatus: string;
  region: string;
  releaseVersion: any;
  version: string;
  solutionId?: string;
  isAMR: any;
  latestReleaseVersion: any;
  tenant: string;

  constructor( private tenantsService: TenantsService,
    private router: Router, private authService: AuthService, private changeTaskService: ChangeTaskService,
              private blueprintsService: BlueprintsService, private dialog: MatDialog,
              private permissionsService: PermissionsService, private trialStatusService: TrialStatusService,
              private solutionsService: SolutionsService, private solutionVersionsService: SolutionVersionsService,
              private toastrService: ToastrService) {
    this.permissionsMap = this.permissionsService.resolveUserPermissions(REQUIRED_PERMISSIONS);
  }

  ngOnInit(): void {
  if(!this.loader) {
    const serviceId = this.serviceCardInfo?.blueprint?.solutionId;
      this.solutionsService.getSolutionVersions(serviceId).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: any) => {
      this.serviceVersions = result;
    const liveInstance = this.serviceCardInfo.instances.find(instance => instance.deploymentClass === DeploymentClass.A);
      if (liveInstance) {
        this.latestReleaseVersion = this.serviceVersions?.filter(version =>
          version.id === liveInstance.solutionVersionId)[0]?.releaseVersion;
      } else {
        const instanceLength = this.serviceCardInfo.instances.length;
        const latestInstanceSolutionVersion = this.serviceCardInfo.instances[instanceLength - 1]?.solutionVersionId;
        this.latestReleaseVersion = this.serviceVersions?.filter(version =>
          version.id === latestInstanceSolutionVersion)[0]?.releaseVersion;
      }

      const demoInstance = this.serviceCardInfo.instances?.find((instance) => instance.blueprintId === this.serviceCardInfo.blueprint.id);
      if (this.latestReleaseVersion) {
        this.releaseVersion = this.latestReleaseVersion;
      } else {
        this.releaseVersion = this.serviceCardInfo?.serviceVersionsInfo?.currentVersion?.releaseVersion;
      }
      if(demoInstance) {
        this.trialStatus = this.trialStatusService.deployStatusUTCFormat(demoInstance);
      } else {
        this.trialStatus = this.trialStatusService.createdStatusUTCFormat(this.serviceCardInfo?.blueprint);
      }

      if (this.serviceCardInfo.serviceVersionsInfo.updateVersion) {
        this.loadChangeTask();
      }
      if(!this.tenantDetails) {
      this.getTenantDetails();
      }
      if(this.releaseVersion && this.solutionVersionsService.releaseVersion(this.releaseVersion)) {
        this.isAMR = true;
      }

      if (this.serviceCardInfo.status === ServiceStatus.LIVE) {
        this.loadBlueprintVersion(this.serviceCardInfo.blueprint.id);
      }
    });
  }
  this.tenant = this.authService.principal.provider;
  }

  loadBlueprintVersion(id: string): void {
    this.blueprintsService.getBlueprintVersion(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: any) => {
        const liveInstanceId = this.serviceCardInfo.blueprint?.pathToLiveConfiguration?.
          find(item => item.deploymentClass == DeploymentClass.A)?.instanceId;
        const blueprintVersionId = this.serviceCardInfo.instances.find(item => item.id === liveInstanceId)?.blueprintVersionId;
        this.serviceCardInfo.serviceDefinitionVersion = result?.find(item => item.id === blueprintVersionId)?.version;
      }, (error) => {
        this.toastrService.clear();
            setTimeout(() => {
		  const title = translate('ERRORS.SERVICE_AVAILABILITY');
          const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
          this.toastrService.errMessage(message, title);
        }, 500);
      });
  }

  getTenantDetails(): void {
    this.tenantsService.getTenantDetails()
		.pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(tenantData => {
      this.region = tenantData?.region;
      // this.region = tenantData?.temenosRegion;
    });

  }
  /**
   * After clicking a service card, the blueprints page will be opened
   */
  goToBlueprint(): void {
    this.router.navigate(['/', this.authService.principal.provider, RouterSegments.SERVICES, this.serviceCardInfo?.service?.id,
      RouterSegments.BLUEPRINTS, this.serviceCardInfo.blueprint.id]);
  }

  deleteBlueprint(): void {
    this.dialog.open(ConfirmationDialogComponent, {
      panelClass: 'default-dialog-wrapper',
      width: '566px',
      autoFocus: false,
      data: {
        message: translate('BLUEPRINT_SUMMARY.CONFIRM_DELETE_BLUEPRINT')
      }
    })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(confirm => {
        if (confirm) {
          this.isLoading = true;
          this.blueprintsService.deleteBlueprint(this.serviceCardInfo.blueprint.id)
          .pipe(
            finalize(() => this.isLoading = false),
            takeUntilDestroyed(this.destroyRef))
          .subscribe(() => {
            this.blueprintDeleted.emit(this.serviceCardInfo);
          },
          (error) => {
            this.dialog.open(ErrorDialogComponent, {
              minHeight: '342px',
              width: '566px',
              panelClass: 'flex-dialog',
              data: {
                title:  translate('NOTIFICATIONS.DELETE_SERVICE_DEFINITION_FAILED'),
                description: error,
              }
            });
          });
        }
      });
  }

  private loadChangeTask(): void {
    this.changeTaskService.getChangeTasks({solutionVersionId: this.serviceCardInfo.serviceVersionsInfo.updateVersion.id,
      blueprintId: this.serviceCardInfo.blueprint.id})
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(changeTasks => {
          if (changeTasks.length) {
            const changeTask = changeTasks[0];
            this.changeTask = changeTask;
            this.isUpdateAvailable = ChangeState.PENDING_APPLICATION === changeTask.changeState
              || ChangeState.APPLYING === changeTask.changeState;
            this.isComingSoon = ChangeState.READY_FOR_TEST === changeTask.changeState;
          } else {
            this.isComingSoon = true;
          }
        });
  }
}
